import _ from 'lodash'
import BIMESSAGES from 'core/data-model/constants/bi-messages'
import MESSAGES from 'core/data-model/constants/messages'
import VALUES from 'core/data-model/constants/values'

import { disclaimers } from 'core/service/pdf-generation/products/common/components/disclaimers'
import { formatNumber } from 'core/service/lib/number-format'
import { is3LevelThaiWord } from 'core/service/pdf-generation/utils/word-style'
import { totalRiderPremium } from 'core/service/basic-plan/premium'
import { getRiderSumAssuredDisplayValue } from 'core/service/benefit-illustration/profile-summary'
import { buildStacksWithStyle, buildHeaderTable } from 'core/service/pdf-generation/utils/builder'
import {
  getRiderYearsOfCoverage,
  getRiderYearsOfPayment,
  hasAsteriskFromRiders,
} from 'core/service/rider/benefits/benefit-data'
import {
  getBasicPlanNameDisplayValue,
  getSumAssuredDisplayValue,
  getRiderNameDisplayValue,
} from 'core/service/pdf-generation/products/common/logic'

import {
  isNoShowAsterisk,
  summaryTable,
  CIESRowsInTable,
} from 'core/service/pdf-generation/products/common/sections/profile-snapshot-section'

const buildBasicPlanRowInTable = (
  basicPlan,
  calculatedCoveragePeriod,
  calculatedPaymentPeriod,
  basicSumAssured,
  basicPremium,
  basicPlanLabel
) => [
  {
    text: getBasicPlanNameDisplayValue(
      // $$FlowFixMe
      _.isFunction(basicPlanLabel)
        ? {
            ...basicPlan,
            // $$FlowFixMe
            name: basicPlanLabel(basicPlan),
          }
        : basicPlan
    ),
  },
  { text: BIMESSAGES.COVERAGE_PERIOD_ISMART_SUFFIX, alignment: 'center' },
  { text: calculatedPaymentPeriod, alignment: 'center' },
  {
    text: getSumAssuredDisplayValue(basicPlan, basicSumAssured, false),
    alignment: 'center',
  },
  { text: formatNumber(basicPremium, 2), bold: true, alignment: 'right' },
]

const buildTotalPremiumRow = (totalPremium) => {
  return [
    { colSpan: 4, rowSpan: 1, text: BIMESSAGES.TOTAL_PREMIUM_FIRST_PREMIUM },
    '',
    '',
    '',
    {
      colSpan: 1,
      rowSpan: 1,
      text: formatNumber(totalPremium, 2),
      bold: true,
      alignment: 'right',
    },
  ]
}

const buildRidersRowInTable = (riders, isNewAsteriskOfSummaryMainContract) => {
  return _.flatMap(riders, (rider) => {
    if (rider.code === 'CIES') {
      return CIESRowsInTable(rider)
    }

    return [
      [
        { text: getRiderNameDisplayValue(rider, isNewAsteriskOfSummaryMainContract) },
        { text: getRiderYearsOfCoverage(rider), alignment: 'center' },
        { text: getRiderYearsOfPayment(rider, { hasAsterisk: true }), alignment: 'center' },
        { text: getRiderSumAssuredDisplayValue(rider), alignment: 'center' },
        { text: formatNumber(rider.premium, 2), bold: true, alignment: 'right' },
      ],
    ]
  })
}

const buildSummaryBasicPlan = (sumAssured, selectedModelFactorLabel) => [
  {
    label: { text: BIMESSAGES.BASIC_PLAN_SUM_ASSURED, style: '' },
    value: {
      text: `${formatNumber(sumAssured)} ${MESSAGES.BAHT}`,
      style: 'basicPlanSummaryValue',
    },
  },
  {
    label: { text: MESSAGES.PAYMENT_MODE, style: '' },
    value: {
      text: selectedModelFactorLabel,
      style: is3LevelThaiWord(selectedModelFactorLabel) ? 'basicPlanSummaryValueSmall' : 'basicPlanSummaryValue',
    },
  },
]

const buildSummaryPremium = (basicPremium, riderPremium, totalPremium, riders) => [
  {
    label: { text: BIMESSAGES.BASIC_PREMIUM, style: '' },
    value: {
      text: `${formatNumber(basicPremium, 2)} ${MESSAGES.BAHT}`,
      style: 'basicPlanSummaryValue',
    },
  },
  {
    label: { text: `${BIMESSAGES.TOTAL_RIDER_PREMIUM}${hasAsteriskFromRiders(riders) ? '*' : ''}`, style: '' },
    value: {
      text: `${formatNumber(riderPremium, 2)} ${MESSAGES.BAHT}`,
      style: 'basicPlanSummaryValue',
    },
  },
  {
    label: { text: BIMESSAGES.TOTAL_PREMIUM_FIRST_PREMIUM, style: '' },
    value: {
      text: `${formatNumber(totalPremium, 2)} ${MESSAGES.BAHT}`,
      style: 'basicPlanSummaryValue',
    },
  },
]

export const hasDisclaimerFiveAsterisksMessage = (riders) => {
  return _.some(riders, (rider) => VALUES.LIST_FIVE_ASTERISKS_BENEFIT_SUMMARY_TABLE.includes(_.get(rider, 'code')))
}

const buildDisclaimers = (basicPlan, riders, isNewAsteriskOfSummaryMainContract) => {
  return {
    stack: disclaimers(basicPlan, riders, isNewAsteriskOfSummaryMainContract).map((disclaimer) => ({
      text: disclaimer,
    })),
    style: 'disclaimer',
  }
}

const buildSummaryTableDisclaimer = () => {
  return {
    stack: [' ', BIMESSAGES.FLEXI_SUMMARY_TABLE_DISCLAIMER_3, BIMESSAGES.FLEXI_SUMMARY_TABLE_DISCLAIMER_4],
    style: 'disclaimerBox',
  }
}

export const profileSnapshotSection = ({
  basicPlan,
  genderLabel,
  age,
  calculatedCoveragePeriod,
  calculatedPaymentPeriod,
  sumAssured,
  selectedModelFactorLabel,
  basicPremium,
  riders,
  hasNonLevelRider,
  totalPremium,
  basicPlanLabel,
  selectedModelFactorID,
  isNewAsteriskOfSummaryMainContract,
  riderCampaignDiscountBIMessage,
  ...props
}) => [
  {
    table: {
      widths: ['33%', '60%'],
      body: [buildStacksWithStyle(buildSummaryBasicPlan(sumAssured, selectedModelFactorLabel))],
    },
    layout: 'noBorders',
  },
  {
    table: {
      widths: ['33%', '34%', '33%'],
      body: [buildStacksWithStyle(buildSummaryPremium(basicPremium, totalRiderPremium(riders), totalPremium, riders))],
    },
    layout: 'noBorders',
    marginBottom: 20,
  },
  riderCampaignDiscountBIMessage.length > 0
    ? riderCampaignDiscountBIMessage.map((riderMessage) => {
        return {
          text: riderMessage,
          color: '#138636',
          alignment: 'center',
          marginBottom: 10,
        }
      })
    : '',
  {
    text: summaryTable(basicPlan, isNewAsteriskOfSummaryMainContract).title,
    style: 'title',
  },
  {
    table: {
      headerRows: 1,
      widths: [150, 'auto', 'auto', 'auto', 'auto'],
      margin: [100, 10],
      body: [
        buildHeaderTable(summaryTable(basicPlan, isNewAsteriskOfSummaryMainContract).headers),
        buildBasicPlanRowInTable(
          basicPlan,
          calculatedCoveragePeriod,
          selectedModelFactorID === 'model-factor_5' ? selectedModelFactorLabel : calculatedPaymentPeriod,
          sumAssured,
          basicPremium,
          basicPlanLabel
        ),
        ...buildRidersRowInTable(riders, isNewAsteriskOfSummaryMainContract),
        buildTotalPremiumRow(totalPremium),
      ],
    },
    layout: 'lightBorder',
    style: 'table',
  },
  isNoShowAsterisk(basicPlan) ? '' : buildDisclaimers(basicPlan, riders, isNewAsteriskOfSummaryMainContract),
  isNoShowAsterisk(basicPlan) ? '' : buildSummaryTableDisclaimer(),
]
